import { FileIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { useTranslation } from '@/translations/i18n'

interface InviteeUploadProgressProps {
	/** Name of the CSV being sent, so the card names what it is working on. */
	fileName: string
	/** The create call is still open; the import has not started yet. */
	isCreating: boolean
}

/**
 * Busy card shown in place of the file picker while a create submit is running.
 *
 * A 300-row file is a few tens of KB, so `onUploadProgress` would reach 100%
 * almost at once and then sit there for the ten seconds the backend spends
 * parsing, deduping and resolving — a bar that stalls full reads worse than no
 * bar at all. What the client can honestly report is which of the two calls is
 * open (POST /events, then POST /events/{id}/import), so the card names the
 * current step instead: the text changing is the signal that the work moved on.
 */
export function InviteeUploadProgress({
	fileName,
	isCreating
}: InviteeUploadProgressProps) {
	const { t } = useTranslation()
	return (
		<div
			aria-busy='true'
			aria-live='polite'
			className='flex items-center gap-3 rounded-[12px] border border-grey-600 px-4 py-3'
		>
			<FileIcon className='shrink-0 text-neutral-500' size={20} />
			<div className='flex min-w-0 flex-1 flex-col'>
				<span className='font-medium text-neutral-900 text-sm'>
					{isCreating
						? t('marquee:import_step_creating')
						: t('marquee:import_step_importing')}
				</span>
				<span className='truncate text-neutral-500 text-xs'>
					{isCreating
						? fileName
						: t('marquee:import_step_importing_hint')}
				</span>
			</div>
			<Spinner className='shrink-0' size={20} />
		</div>
	)
}
